import { useMemo } from 'react'
import styles from './InstagramPostPreview.pcss'
import threadsStyles from './ThreadsPostPreview.pcss'
import { PostPreviewDefaultHeader } from './PostPreviewDefaultHeader'
import PlayIcon from '@mui/icons-material/PlayArrowRounded'
import { PostPreviewVideoPlayer } from './PostPreviewVideoPlayer'
import PostPreviewText from './PostPreviewText'
import ImageCarousel from 'components/ImageCarousel'
import IconError from '@mui/icons-material/ErrorOutlineRounded'
import { getDomainFromUrl } from 'shared'

const MAX_LINES = 2
const CAROUSEL_WIDTH = 398
const TRUNCATE_TEXT = 'more'
const COLLAPSE_TEXT_LABEL = 'less'

interface ThreadsPostPreviewProps {
  profile?: { name: string, image: string }
  featuredImageUrl?: string
  imageUrls?: string[]
  empty?: boolean
  statusText?: string
  videoUrl?: string
  link?: {
    url: string
    title: string
    description?: string
  }
}

export function ThreadsPostPreview(props: ThreadsPostPreviewProps) {
  const isEmpty = props.empty || (!props.statusText && !props.imageUrls && !props.videoUrl && !props.link)
  const additionalImages = props.imageUrls ? props.imageUrls.filter(url => url !== props.featuredImageUrl) : []
  const carouselImages = [props.featuredImageUrl].concat(additionalImages).filter(Boolean) as string[]
  const noImgClass = props.imageUrls?.length === 0 ? styles['no-img'] : ''
  const videoLinkImage = props.imageUrls?.[0] || props.featuredImageUrl || ''

  const videoLinkImgStyle = {
    backgroundImage: `url(${videoLinkImage})`
  }

  const linkDomain = useMemo(() => {
    if (!props.link) {
      return ''
    }
    const match = props.link.url.match(/:\/\/(www\.)?(.[^/]+)/)
    if (!match) {
      return ''
    }
    return match[2] || ''
  }, [props.link])

  return (
    <div
      className={`${styles.post} ${styles.threads} ${noImgClass} ${isEmpty ? styles.empty : ''}`}
      data-testid="threads-post-preview"
    >
      <PostPreviewDefaultHeader instagram profile={props.profile} />
      {props.statusText && (
        <PostPreviewText
          text={props.statusText || ''}
          maxLinesDefault={MAX_LINES}
          expandLabel={TRUNCATE_TEXT}
          collapseLabel={COLLAPSE_TEXT_LABEL}
          className={styles['ig-text']}
        />
      )}
      {!props.videoUrl && props.featuredImageUrl && !props.link && (
        <ImageCarousel key={props.featuredImageUrl} imageUrls={carouselImages} width={CAROUSEL_WIDTH} />
      )}
      {isEmpty && (
        <div className={styles['img-box']}></div>
      )}
      {props.videoUrl && (
        <PostPreviewVideoPlayer
          videoUrl={props.videoUrl}
          icon={<PlayIcon className={`${styles['icon-centered']} ${styles.icon}`} />}
        />
      )}
      {props.link && (
        <div className={threadsStyles['video-link-container']}>
          <div className={threadsStyles['video-link-img']} style={videoLinkImgStyle}></div>
          <div className={threadsStyles['video-link-bottom']}>
            <div className={threadsStyles.link}>{linkDomain}</div>
            <div className={threadsStyles['video-link-title']}>{props.link.title}</div>
          </div>
        </div>
      )}
      <footer className={styles.footer}>
        <div className={styles.icons}>
          {/* eslint-disable max-len */}
          <svg aria-label="Like" color="rgb(38, 38, 38)" fill="rgb(38, 38, 38)" height="24" role="img" viewBox="0 0 24 24" width="24"><title>Like</title><path d="M16.792 3.904A4.989 4.989 0 0 1 21.5 9.122c0 3.072-2.652 4.959-5.197 7.222-2.512 2.243-3.865 3.469-4.303 3.752-.477-.309-2.143-1.823-4.303-3.752C5.141 14.072 2.5 12.167 2.5 9.122a4.989 4.989 0 0 1 4.708-5.218 4.21 4.21 0 0 1 3.675 1.941c.84 1.175.98 1.763 1.12 1.763s.278-.588 1.11-1.766a4.17 4.17 0 0 1 3.679-1.938m0-2a6.04 6.04 0 0 0-4.797 2.127 6.052 6.052 0 0 0-4.787-2.127A6.985 6.985 0 0 0 .5 9.122c0 3.61 2.55 5.827 5.015 7.97.283.246.569.494.853.747l1.027.918a44.998 44.998 0 0 0 3.518 3.018 2 2 0 0 0 2.174 0 45.263 45.263 0 0 0 3.626-3.115l.922-.824c.293-.26.59-.519.885-.774 2.334-2.025 4.98-4.32 4.98-7.94a6.985 6.985 0 0 0-6.708-7.218Z"></path></svg>
          <svg aria-label="Comment" color="rgb(38, 38, 38)" fill="rgb(38, 38, 38)" height="24" role="img" viewBox="0 0 24 24" width="24"><title>Comment</title><path d="M20.656 17.008a9.993 9.993 0 1 0-3.59 3.615L22 22Z" fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth="2"></path></svg>
          <svg aria-label="Repost" role="img" viewBox="0 0 18 18"><title>Repost</title><path d="M6.41256 1.23531C6.6349 0.971277 7.02918 0.937481 7.29321 1.15982L9.96509 3.40982C10.1022 3.52528 10.1831 3.69404 10.1873 3.87324C10.1915 4.05243 10.1186 4.2248 9.98706 4.34656L7.31518 6.81971C7.06186 7.05419 6.66643 7.03892 6.43196 6.7856C6.19748 6.53228 6.21275 6.13685 6.46607 5.90237L7.9672 4.51289H5.20312C3.68434 4.51289 2.45312 5.74411 2.45312 7.26289V9.51289V11.7629C2.45312 13.2817 3.68434 14.5129 5.20312 14.5129C5.5483 14.5129 5.82812 14.7927 5.82812 15.1379C5.82812 15.4831 5.5483 15.7629 5.20312 15.7629C2.99399 15.7629 1.20312 13.972 1.20312 11.7629V9.51289V7.26289C1.20312 5.05375 2.99399 3.26289 5.20312 3.26289H7.85002L6.48804 2.11596C6.22401 1.89362 6.19021 1.49934 6.41256 1.23531Z"></path><path d="M11.5874 17.7904C11.3651 18.0545 10.9708 18.0883 10.7068 17.8659L8.03491 15.6159C7.89781 15.5005 7.81687 15.3317 7.81267 15.1525C7.80847 14.9733 7.8814 14.801 8.01294 14.6792L10.6848 12.206C10.9381 11.9716 11.3336 11.9868 11.568 12.2402C11.8025 12.4935 11.7872 12.8889 11.5339 13.1234L10.0328 14.5129H12.7969C14.3157 14.5129 15.5469 13.2816 15.5469 11.7629V9.51286V7.26286C15.5469 5.74408 14.3157 4.51286 12.7969 4.51286C12.4517 4.51286 12.1719 4.23304 12.1719 3.88786C12.1719 3.54269 12.4517 3.26286 12.7969 3.26286C15.006 3.26286 16.7969 5.05373 16.7969 7.26286V9.51286V11.7629C16.7969 13.972 15.006 15.7629 12.7969 15.7629H10.15L11.512 16.9098C11.776 17.1321 11.8098 17.5264 11.5874 17.7904Z"></path></svg>
          <svg aria-label="Share Post" color="rgb(38, 38, 38)" fill="rgb(38, 38, 38)" height="24" role="img" viewBox="0 0 24 24" width="24"><title>Share Post</title><line fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth="2" x1="22" x2="9.218" y1="3" y2="10.083"></line><polygon fill="none" points="11.698 20.334 22 3.001 2 3.001 9.218 10.084 11.698 20.334" stroke="currentColor" strokeLinejoin="round" strokeWidth="2"></polygon></svg>
          {/* eslint-enable max-len */}
        </div>
      </footer>
    </div>
  )
}
